import React, { useState, useEffect, useMemo } from 'react';
import { Panel, Button, Badge } from '../../design/primitives';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../../design/tokens';
import { apiClient } from '../../api/apiConfig';
import { MetricChart, Spinner } from '../ui';
import {
  GroupedAccountsResponse,
  NetWorthHistoryPoint,
  FinancialAccount,
  BudgetMonthlySummary,
} from '../../types/finance';

const formatCurrency = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

const PERIODS = [
  { label: '1M', months: 1 },
  { label: '3M', months: 3 },
  { label: '6M', months: 6 },
  { label: '1Y', months: 12 },
  { label: 'All', months: 60 },
];

const BUDGET_PERIODS = [
  { label: '6M', months: 6 },
  { label: 'YTD', months: 0 }, // 0 = compute from Jan
];

interface Driver {
  name: string;
  change: number;
  nw_impact: number;
  is_asset: boolean;
}

interface PeriodChange {
  change: number;
  pct: number;
  ref_date: string;
  ref_nw: number;
  drivers: Driver[];
}

interface ChangesData {
  current_net_worth: number;
  date: string;
  daily: PeriodChange | null;
  mtd: PeriodChange | null;
  ytd: PeriodChange | null;
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

function staleDays(dateStr: string): number {
  return Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
}

const ChangeCard: React.FC<{ label: string; period: PeriodChange | null }> = ({ label, period }) => {
  if (!period) {
    return (
      <div style={{
        flex: 1,
        padding: SPACING[12],
        backgroundColor: COLORS.bg.panel,
        borderRadius: RADIUS,
        border: `1px solid ${COLORS.border.default}`,
        minWidth: 0,
      }}>
        <div style={{ fontSize: TYPOGRAPHY.body.sizes.small, color: COLORS.text.tertiary, marginBottom: SPACING[4] }}>
          {label}
        </div>
        <div style={{ fontSize: TYPOGRAPHY.body.sizes.small, color: COLORS.text.tertiary }}>
          --
        </div>
      </div>
    );
  }

  const isPositive = period.change >= 0;
  const color = isPositive ? COLORS.status.success : COLORS.status.error;
  const arrow = isPositive ? '\u25B2' : '\u25BC';

  return (
    <div style={{
      flex: 1,
      padding: SPACING[12],
      backgroundColor: COLORS.bg.panel,
      borderRadius: RADIUS,
      border: `1px solid ${COLORS.border.default}`,
      minWidth: 0,
    }}>
      <div style={{ fontSize: TYPOGRAPHY.body.sizes.small, color: COLORS.text.tertiary, marginBottom: SPACING[4] }}>
        {label}
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: SPACING[8], flexWrap: 'wrap' }}>
        <span style={{
          fontFamily: TYPOGRAPHY.headings.fontFamily,
          fontSize: TYPOGRAPHY.headings.sizes.h3,
          fontWeight: '700',
          color,
        }}>
          {arrow} {formatCurrency(Math.abs(period.change))}
        </span>
        <span style={{ fontSize: TYPOGRAPHY.body.sizes.small, color, fontWeight: '600' }}>
          {isPositive ? '+' : ''}{period.pct.toFixed(2)}%
        </span>
      </div>
    </div>
  );
};

const DriversPanel: React.FC<{ changes: ChangesData }> = ({ changes }) => {
  const period = changes.daily || changes.mtd || changes.ytd;
  if (!period || period.drivers.length === 0) return null;

  const periodLabel = changes.daily ? 'Today' : changes.mtd ? 'This Month' : 'This Year';

  return (
    <Panel>
      <div style={{
        fontFamily: TYPOGRAPHY.headings.fontFamily,
        fontSize: TYPOGRAPHY.headings.sizes.h3,
        fontWeight: TYPOGRAPHY.headings.weight,
        color: COLORS.text.primary,
        marginBottom: SPACING[12],
      }}>
        Main Drivers ({periodLabel})
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[4] }}>
        {period.drivers.map((d, i) => {
          const isPositive = d.nw_impact >= 0;
          const color = isPositive ? COLORS.status.success : COLORS.status.error;
          return (
            <div key={i} style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: `${SPACING[8]} 0`,
              borderBottom: i < period.drivers.length - 1 ? `1px solid ${COLORS.border.default}` : 'none',
              fontSize: TYPOGRAPHY.body.sizes.default,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[8] }}>
                <div style={{
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  backgroundColor: color,
                  flexShrink: 0,
                }} />
                <span style={{ color: COLORS.text.primary }}>{d.name}</span>
              </div>
              <span style={{ fontWeight: '600', color, fontSize: TYPOGRAPHY.body.sizes.small }}>
                {isPositive ? '+' : ''}{formatCurrency(d.nw_impact)}
              </span>
            </div>
          );
        })}
      </div>
    </Panel>
  );
};

const FinanceSummary: React.FC = () => {
  const [history, setHistory] = useState<NetWorthHistoryPoint[]>([]);
  const [grouped, setGrouped] = useState<GroupedAccountsResponse | null>(null);
  const [changes, setChanges] = useState<ChangesData | null>(null);
  const [budgetSummary, setBudgetSummary] = useState<BudgetMonthlySummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [nwMonths, setNwMonths] = useState(12);
  const [budgetMonths, setBudgetMonths] = useState(6);

  const fetchCore = async (showSpinner = true) => {
    if (showSpinner) setLoading(true);
    try {
      const [grpRes, budRes, chgRes] = await Promise.all([
        apiClient.get('/api/financial-accounts/grouped/'),
        apiClient.get(`/api/budgets/monthly_summary/?months=${budgetMonths || ytdMonths()}`),
        apiClient.get('/api/net-worth/changes/'),
      ]);
      setGrouped(grpRes.data);
      setBudgetSummary(budRes.data);
      setChanges(chgRes.data);
    } catch (err) {
      console.error('Failed to fetch finance summary', err);
    } finally {
      if (showSpinner) setLoading(false);
    }
  };

  // Initial load + auto-refresh
  useEffect(() => {
    fetchCore(true);
    const interval = setInterval(() => fetchCore(false), 60_000);
    return () => clearInterval(interval);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Net worth history: refetch on period change
  useEffect(() => {
    const load = async () => {
      try {
        const res = await apiClient.get(`/api/net-worth/history/?months=${nwMonths}`);
        setHistory(res.data);
      } catch (err) {
        console.error('Failed to fetch net worth history', err);
      }
    };
    load();
  }, [nwMonths]);

  // Budget summary: refetch on period change
  useEffect(() => {
    const load = async () => {
      try {
        const m = budgetMonths || ytdMonths();
        const res = await apiClient.get(`/api/budgets/monthly_summary/?months=${m}`);
        setBudgetSummary(res.data);
      } catch (err) {
        console.error('Failed to fetch budget summary', err);
      }
    };
    load();
  }, [budgetMonths]);

  const nwChartData = useMemo(() => {
    if (history.length === 0) return [];
    const dates = history.map(h => new Date(h.date));
    const spanDays = (dates[dates.length - 1].getTime() - dates[0].getTime()) / 86400000;
    const fmt: Intl.DateTimeFormatOptions = Math.abs(spanDays) <= 60
      ? { month: 'short', day: 'numeric' }
      : Math.abs(spanDays) <= 365
        ? { month: 'short', year: '2-digit' }
        : { month: 'short', year: 'numeric' };
    return history.map(h => ({
      date: new Date(h.date).toLocaleDateString('en-US', fmt),
      net_worth: h.net_worth,
    }));
  }, [history]);

  const budgetChartData = useMemo(() =>
    budgetSummary.map(b => ({
      date: new Date(b.month).toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
      total_budgeted: b.total_budgeted,
      total_spent: b.total_spent,
    })),
  [budgetSummary]);

  // Flatten all accounts for staleness table
  const allAccounts = useMemo(() => {
    if (!grouped) return [];
    const accts: FinancialAccount[] = [];
    const groups = ['investments', 'cash', 'credit_cards', 'real_estate', 'vehicles', 'other_liabilities'] as const;
    for (const key of groups) {
      const group = grouped[key];
      if (group && 'accounts' in group) {
        accts.push(...group.accounts);
      }
    }
    // Sort most stale first; manual accounts (no plaid_account_id, no last_synced_at) at end
    return accts.sort((a, b) => {
      const aTime = a.last_synced_at ? new Date(a.last_synced_at).getTime() : Infinity;
      const bTime = b.last_synced_at ? new Date(b.last_synced_at).getTime() : Infinity;
      return aTime - bTime;
    });
  }, [grouped]);

  const currentNW = grouped?.summary.net_worth ?? 0;

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: SPACING[32] }}>
        <Spinner size="md" />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[16] }}>
      {/* A. Net Worth + Variation */}
      <Panel>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: SPACING[12] }}>
          <div>
            <div style={{
              fontFamily: TYPOGRAPHY.headings.fontFamily,
              fontSize: TYPOGRAPHY.headings.sizes.h1,
              fontWeight: '700',
              color: COLORS.text.primary,
            }}>
              {formatCurrency(currentNW)}
            </div>
            <div style={{ fontSize: TYPOGRAPHY.body.sizes.small, color: COLORS.text.secondary }}>
              Net Worth
            </div>
          </div>
          <div style={{ display: 'flex', gap: SPACING[4] }}>
            {PERIODS.map(p => (
              <Button
                key={p.months}
                variant={nwMonths === p.months ? 'primary' : 'ghost'}
                onClick={() => setNwMonths(p.months)}
                style={{ padding: `${SPACING[4]} ${SPACING[12]}` }}
              >
                {p.label}
              </Button>
            ))}
          </div>
        </div>

        {/* Daily / MTD / YTD Cards */}
        {changes && (
          <div style={{ display: 'flex', gap: SPACING[8], marginBottom: SPACING[16] }}>
            <ChangeCard label="Daily" period={changes.daily} />
            <ChangeCard label="MTD" period={changes.mtd} />
            <ChangeCard label="YTD" period={changes.ytd} />
          </div>
        )}

        {nwChartData.length > 0 && (
          <MetricChart
            data={nwChartData}
            series={[{ dataKey: 'net_worth', name: 'Net Worth', metricType: 'currency', color: COLORS.accent.default }]}
            type="line"
            height={250}
          />
        )}
      </Panel>

      {/* B. Main Drivers */}
      {changes && <DriversPanel changes={changes} />}

      {/* C. Account Status */}
      <Panel>
        <div style={{
          fontFamily: TYPOGRAPHY.headings.fontFamily,
          fontSize: TYPOGRAPHY.headings.sizes.h3,
          fontWeight: TYPOGRAPHY.headings.weight,
          color: COLORS.text.primary,
          marginBottom: SPACING[12],
        }}>
          Account Status
        </div>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {allAccounts.map(account => {
            const synced = account.last_synced_at;
            const isManual = !account.plaid_account_id && !synced;
            const days = synced ? staleDays(synced) : null;
            let badgeVariant: 'success' | 'warning' | 'danger' | 'default' = 'success';
            let badgeLabel = synced ? timeAgo(synced) : 'Manual';
            if (isManual) {
              badgeVariant = 'default';
              badgeLabel = 'Manual';
            } else if (days !== null && days > 30) {
              badgeVariant = 'danger';
            } else if (days !== null && days > 7) {
              badgeVariant = 'warning';
            }

            return (
              <div key={account.id} style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: `${SPACING[8]} 0`,
                borderBottom: `1px solid ${COLORS.border.default}`,
              }}>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{
                    color: COLORS.text.primary,
                    fontWeight: '500',
                    fontSize: TYPOGRAPHY.body.sizes.default,
                  }}>
                    {account.name}
                  </span>
                  <span style={{ fontSize: TYPOGRAPHY.body.sizes.tiny, color: COLORS.text.tertiary }}>
                    {account.account_type_display}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[12] }}>
                  <span style={{ fontWeight: '600', color: COLORS.text.primary, fontSize: TYPOGRAPHY.body.sizes.default }}>
                    {formatCurrency(account.current_balance)}
                  </span>
                  <Badge variant={badgeVariant} size="sm">{badgeLabel}</Badge>
                </div>
              </div>
            );
          })}
        </div>
      </Panel>

      {/* D. Budget vs Actual */}
      <Panel>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: SPACING[12] }}>
          <div style={{
            fontFamily: TYPOGRAPHY.headings.fontFamily,
            fontSize: TYPOGRAPHY.headings.sizes.h3,
            fontWeight: TYPOGRAPHY.headings.weight,
            color: COLORS.text.primary,
          }}>
            Budget vs Actual
          </div>
          <div style={{ display: 'flex', gap: SPACING[4] }}>
            {BUDGET_PERIODS.map(p => (
              <Button
                key={p.label}
                variant={budgetMonths === p.months ? 'primary' : 'ghost'}
                onClick={() => setBudgetMonths(p.months)}
                style={{ padding: `${SPACING[4]} ${SPACING[12]}` }}
              >
                {p.label}
              </Button>
            ))}
          </div>
        </div>
        {budgetChartData.length > 0 && (
          <MetricChart
            data={budgetChartData}
            series={[
              { dataKey: 'total_budgeted', name: 'Budget', metricType: 'currency', color: COLORS.accent.default },
              { dataKey: 'total_spent', name: 'Actual', metricType: 'currency', color: COLORS.status.error },
            ]}
            type="bar"
            height={250}
          />
        )}
      </Panel>
    </div>
  );
};

function ytdMonths(): number {
  return new Date().getMonth() + 1;
}

export default FinanceSummary;
